import React from 'react';
import {
  FiHome,
  FiUser,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye
} from 'react-icons/fi';

// Types
interface ClaimUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface ClaimCompany {
  id: string;
  slug: string;
  nameTh: string;
  nameEn?: string;
}

export interface Claim {
  id: string;
  userId: string;
  companyId: string;
  isApproved: boolean;
  isActive: boolean;
  notes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  user?: ClaimUser;
  company?: ClaimCompany;
}

interface ClaimCardProps {
  claim: Claim;
  onApprove: (claimId: string, companyName: string) => void;
  onReject: (claimId: string, companyName: string) => void;
  onViewDetails: (claim: Claim) => void;
  actionLoading: boolean;
}

const ClaimCard: React.FC<ClaimCardProps> = ({
  claim,
  onApprove,
  onReject,
  onViewDetails,
  actionLoading
}) => {
  // Helper function to get status
  const getClaimStatus = (): 'pending' | 'approved' | 'rejected' => {
    if (claim.isApproved && claim.isActive) {
      return 'approved';
    } else if (!claim.isApproved && claim.isActive === false) {
      return 'rejected';
    }
    return 'pending';
  };

  const status = getClaimStatus();

  // Status badge configuration
  const statusConfig = {
    pending: {
      color: 'bg-amber-100 text-amber-800 border-amber-200',
      icon: <FiClock className="h-3.5 w-3.5" />,
      label: 'รอดำเนินการ'
    },
    approved: {
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: <FiCheckCircle className="h-3.5 w-3.5" />,
      label: 'อนุมัติแล้ว'
    },
    rejected: {
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: <FiXCircle className="h-3.5 w-3.5" />,
      label: 'ปฏิเสธแล้ว'
    }
  };

  const currentStatus = statusConfig[status];

  // Format date with time
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow duration-200">
      {/* Header with Status Badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {/* Company Name */}
          <div className="flex items-center mb-2">
            <FiHome className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {claim.company?.nameTh || 'ไม่ระบุชื่อบริษัท'}
              </h3>
              {claim.company?.nameEn && (
                <p className="text-sm text-gray-500">{claim.company.nameEn}</p>
              )}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${currentStatus.color}`}
        >
          {currentStatus.icon}
          {currentStatus.label}
        </span>
      </div>

      {/* User Info */}
      <div className="flex items-center mb-3 text-gray-700">
        <FiUser className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
        <div className="flex-1">
          <span className="font-medium">
            {claim.user?.firstName && claim.user?.lastName
              ? `${claim.user.firstName} ${claim.user.lastName}`
              : 'ไม่ระบุชื่อ'}
          </span>
          <span className="text-sm text-gray-500 ml-2">
            ({claim.user?.email || 'ไม่มีอีเมล'})
          </span>
        </div>
      </div>

      {/* Created Date */}
      <div className="flex items-center mb-4 text-sm text-gray-600">
        <FiCalendar className="h-4 w-4 mr-2 text-gray-400" />
        <span>ส่งคำขอเมื่อ: {formatDate(claim.createdAt)}</span>
      </div>

      {/* Notes/Rejection Reason */}
      {(claim.notes || claim.rejectionReason) && (
        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-700">
            {status === 'rejected' && claim.rejectionReason ? (
              <>
                <span className="font-medium text-red-600">เหตุผลที่ปฏิเสธ:</span>{' '}
                {claim.rejectionReason}
              </>
            ) : (
              <>
                <span className="font-medium">หมายเหตุ:</span> {claim.notes}
              </>
            )}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        {/* View Details Button */}
        <button
          onClick={() => onViewDetails(claim)}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <FiEye className="mr-2 h-4 w-4" />
          ดูรายละเอียด
        </button>

        {/* Approve/Reject Buttons (only for pending) */}
        {status === 'pending' && (
          <>
            <button
              onClick={() => onApprove(claim.id, claim.company?.nameTh || 'บริษัท')}
              disabled={actionLoading}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiCheckCircle className="mr-2 h-4 w-4" />
              อนุมัติ
            </button>
            <button
              onClick={() => onReject(claim.id, claim.company?.nameTh || 'บริษัท')}
              disabled={actionLoading}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiXCircle className="mr-2 h-4 w-4" />
              ปฏิเสธ
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimCard;
